import { useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { toast } from "@jti/core-components/components/Toaster";
import { isServerSide } from "@jti/fe-common";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { LoadingLayer } from "../loading-layer/LoadingLayer";

import type { Purpose } from "./getUser";
import { getUser } from "./getUser";
import { getRedirectionUrl } from "./helpers";

import "./style.scss";

type HiddenPageRedirectionLayerType = {
  userShouldBeAgeVerified: boolean;
  userShouldDeviceBeRegistered: boolean;
  redirectPageForNotAllowedUsers: string;
  redirectToAuthPage: boolean;
  requiredConsentPurposes: Purpose[];
  redirectTime: number;
  isInEditor?: boolean;
};

const checkIsAuthor = (isInEditor?: boolean) => {
  if (isServerSide()) {
    return false;
  }
  return !!window.Granite || isInEditor;
};

const HiddenPageRedirectionLayer: React.FC<HiddenPageRedirectionLayerType> = ({
  userShouldBeAgeVerified = false,
  userShouldDeviceBeRegistered,
  redirectPageForNotAllowedUsers = "",
  redirectToAuthPage = true,
  requiredConsentPurposes = [],
  redirectTime = 3000,
  isInEditor = false,
}) => {
  const [isLoading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const isAuthor = checkIsAuthor(isInEditor);
  const { __ } = useTranslations();
  const resolveAuthHref = useAuthHrefResolver();
  const { REDIRECT_AFTER_LOGIN_URL } = useConfig();

  useEffect(() => {
    if (isAuthor) {
      return;
    }

    let timeout: number | undefined;

    const handleUserValidation = async () => {
      const user = await getUser(requiredConsentPurposes, userShouldBeAgeVerified, userShouldDeviceBeRegistered);

      if (!user?.isLoggedIn || !user.hasUserValidPurposes || !user.isAgeVerified || !user.isDeviceRegistered) {
        setShowToast(true);

        timeout = window.setTimeout(() => {
          window.location.href = getRedirectionUrl(
            redirectPageForNotAllowedUsers,
            redirectToAuthPage,
            user?.isLoggedIn,
            resolveAuthHref,
            REDIRECT_AFTER_LOGIN_URL
          );
        }, redirectTime);

        return;
      }

      setLoading(false);
    };

    handleUserValidation();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      window.document.querySelector(".protectedBodyPage")?.classList.remove("protectedBodyPage");
      window.dispatchEvent(new Event("resize"));
    }
  }, [isLoading]);

  useEffect(() => {
    if (showToast) {
      toast.success(__("hiddenPage.redirect.label"));
    }
  }, [showToast, __]);

  return <LoadingLayer transparentBackground={false} isVisible={!isAuthor && isLoading} testId={"hiddenPageLayer"} />;
};

export default HiddenPageRedirectionLayer;
